import { useCallback, useEffect, useState } from "react";

/**
 * useFilters
 * Per-column filter sets + the popup-open state.  Exposes
 * `isRowVisible(rowIndex)` which combines filters with any externally
 * hidden rows passed in (so the grid only ever consults one predicate).
 *
 * Filters reset whenever the active sheet changes — Excel does the same.
 */
export default function useFilters({ activeSheet, normalizedRows, hiddenRows }) {
  const [filters, setFilters] = useState({});
  const [activeFilterCol, setActiveFilterCol] = useState(null);

  // Reset filter state on sheet switch.
  useEffect(() => {
    setFilters({});
    setActiveFilterCol(null);
  }, [activeSheet]);

  // Close the filter menu on outside click.
  useEffect(() => {
    if (activeFilterCol === null) return undefined;
    const onClick = () => setActiveFilterCol(null);
    window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, [activeFilterCol]);

  const toggleFilterMenu = useCallback((colIndex, event) => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    setActiveFilterCol((prev) => (prev === colIndex ? null : colIndex));
  }, []);

  const applyFilter = useCallback((colIndex, selectedValues) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (!selectedValues) delete next[colIndex];
      else next[colIndex] = selectedValues;
      return next;
    });
  }, []);

  const isRowVisible = useCallback((rowIndex) => {
    // Row 0 is always visible — it's the header row that the freeze
    // pane and filter UI hang off.
    if (rowIndex === 0) return true;
    if (hiddenRows.has(rowIndex)) return false;
    if (Object.keys(filters).length === 0) return true;
    for (const colIndex in filters) {
      const allowed = filters[colIndex];
      if (allowed && allowed.size > 0) {
        const v = normalizedRows[rowIndex]?.[colIndex];
        const strVal = v == null || v === "" ? "(Blank)" : String(v);
        if (!allowed.has(strVal)) return false;
      }
    }
    return true;
  }, [filters, normalizedRows, hiddenRows]);

  return {
    filters, activeFilterCol, setActiveFilterCol,
    toggleFilterMenu, applyFilter, isRowVisible,
  };
}
